import { inject, Injectable } from '@angular/core';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { TorchCaptureCopy } from '@jump-tech-frontend/media-capture';
import { PhotoSourceCopy } from './modals/photo-source-modal.component';
import { TorchCaptureLauncher } from './torch-capture-launcher.service';
import { TorchCaptureSettings } from './torch-capture-settings.service';

export type CameraI18nKeys = Pick<
  I18nKeys,
  | 'cameraTakePhoto'
  | 'cameraCancel'
  | 'cameraPhotosAndFiles'
  | 'cameraTorchOn'
  | 'cameraTorchOff'
  | 'cameraStarting'
  | 'cameraTurningLightOn'
  | 'cameraCapturing'
>;

export const cameraCopyFrom = (i18ns: CameraI18nKeys): TorchCaptureCopy => ({
  shutter: i18ns.cameraTakePhoto,
  cancel: i18ns.cameraCancel,
  torchOn: i18ns.cameraTorchOn,
  torchOff: i18ns.cameraTorchOff,
  starting: i18ns.cameraStarting,
  turningLightOn: i18ns.cameraTurningLightOn,
  capturing: i18ns.cameraCapturing
});

export const photoSourceCopyFrom = (i18ns: CameraI18nKeys): PhotoSourceCopy => ({
  takePhoto: i18ns.cameraTakePhoto,
  photosAndFiles: i18ns.cameraPhotosAndFiles,
  cancel: i18ns.cameraCancel
});

@Injectable({ providedIn: 'root' })
export class PhotoCaptureTrigger {
  private readonly settings = inject(TorchCaptureSettings);
  private readonly launcher = inject(TorchCaptureLauncher);

  public isAvailable(): boolean {
    return this.settings.isAvailable();
  }

  public async open(fileInput: HTMLInputElement, i18ns: CameraI18nKeys): Promise<string | null> {
    if (!this.isAvailable()) {
      fileInput.click();
      return null;
    }

    return this.capture(() => fileInput.click(), i18ns);
  }

  public async capture(openPicker: () => void, i18ns: CameraI18nKeys): Promise<string | null> {
    const source = await this.launcher.chooseSource(photoSourceCopyFrom(i18ns), openPicker);
    if (source !== 'camera') {
      return null;
    }

    const outcome = await this.launcher.capture(cameraCopyFrom(i18ns));
    if (outcome.kind === 'unavailable') {
      this.settings.ruleOut();
    }
    return outcome.kind === 'captured' ? outcome.image : null;
  }
}
